import { ReactNode, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { 
  LayoutDashboard, 
  Users, 
  CheckCircle, 
  CreditCard,
  Ticket, 
  Calendar, 
  Star, 
  Edit3, 
  DollarSign, 
  BarChart3, 
  Settings,
  Award,
  Bell,
  Flag,
  Handshake,
  Briefcase,
  LucideIcon,
  Menu,
  ChevronDown,
  Tags,
  ShieldBan,
  Rss
} from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarProvider,
  SidebarTrigger
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface AdminLayoutProps {
  children: ReactNode;
}

interface MenuItemProps {
  href: string;
  icon: LucideIcon;
  children: ReactNode;
}

interface DropdownItem {
  href: string;
  icon: LucideIcon;
  label: string;
}

interface DropdownMenuProps {
  icon: LucideIcon;
  label: string;
  items: DropdownItem[];
  isOpen: boolean;
  onToggle: () => void;
}

const AdminMenuItem = ({ href, icon: Icon, children }: MenuItemProps) => {
  const location = useLocation();
  const isActive = location.pathname === href;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild>
        <Link
          to={href}
          className={cn(
            "flex items-center gap-3 px-4 py-3 mb-2 transition-all font-heading font-bold uppercase text-sm tracking-wide leading-none",
            "border-[3px] border-dark bg-white",
            isActive
              ? "bg-primary text-dark shadow-[4px_4px_0px_0px] shadow-dark"
              : "text-foreground hover:bg-primary hover:text-dark hover:shadow-[2px_2px_0px_0px] hover:shadow-dark hover:translate-x-[-1px] hover:translate-y-[-1px]"
          )}
        >
          <Icon className="w-5 h-5 flex-shrink-0" />
          <span>{children}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const AdminMenuDropdown = ({ icon: Icon, label, items, isOpen, onToggle }: DropdownMenuProps) => {
  const location = useLocation();
  const hasActiveChild = items.some(item => location.pathname === item.href);

  return (
    <SidebarMenuItem>
      <Collapsible open={isOpen} onOpenChange={onToggle} className="w-full">
        <SidebarMenuButton asChild>
          <CollapsibleTrigger asChild>
            <div
              className={cn(
                "flex items-center gap-3 px-4 py-3 mb-2 transition-all font-heading font-bold uppercase text-sm tracking-wide leading-none cursor-pointer",
                "border-[3px] border-dark bg-white",
                hasActiveChild 
                  ? "bg-primary/30" 
                  : "hover:bg-primary hover:text-dark hover:shadow-[2px_2px_0px_0px] hover:shadow-dark hover:translate-x-[-1px] hover:translate-y-[-1px]"
              )}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1">{label}</span>
              <ChevronDown 
                className={cn(
                  "w-4 h-4 flex-shrink-0 transition-transform duration-200", 
                  isOpen && "rotate-180"
                )} 
              />
            </div>
          </CollapsibleTrigger>
        </SidebarMenuButton>
        <CollapsibleContent>
          <div className="ml-2 border-l-[3px] border-dark/30 pl-1 mb-2">
            <SidebarMenu>
              {items.map((item) => (
                <AdminMenuItem 
                  key={item.href} 
                  href={item.href} 
                  icon={item.icon}
                >
                  {item.label}
                </AdminMenuItem>
              ))}
            </SidebarMenu>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenuItem>
  );
};

const AdminSidebarContent = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  // Menu structure with translations
  const topItems = [
    { href: "/admin", icon: LayoutDashboard, label: t("admin.sidebar.dashboard") },
    { href: "/admin/notifications", icon: Bell, label: t("admin.sidebar.notifications") },
    { href: "/admin/partnerships", icon: Handshake, label: t("admin.sidebar.partnerships") },
  ];

  const usersDropdown = {
    icon: Users,
    label: t("admin.sidebar.usersGroup"),
    items: [
      { href: "/admin/users", icon: Users, label: t("admin.sidebar.allUsers") },
      { href: "/admin/screening", icon: CheckCircle, label: t("admin.sidebar.screening") },
      { href: "/admin/reports", icon: Flag, label: t("admin.sidebar.reports") },
      { href: "/admin/badges", icon: Award, label: t("admin.sidebar.badgesRewards") },
      { href: "/admin/blocked-ips", icon: ShieldBan, label: t("admin.sidebar.blockedIps") },
    ]
  };

  const middleItems = [
    { href: "/admin/subscriptions", icon: CreditCard, label: t("admin.sidebar.subscriptions") },
    { href: "/admin/coupons", icon: Ticket, label: t("admin.sidebar.coupons") },
    { href: "/admin/events", icon: Calendar, label: t("admin.sidebar.events") },
    { href: "/admin/jobs", icon: Briefcase, label: t("admin.sidebar.jobs") },
    { href: "/admin/reviews", icon: Star, label: t("admin.sidebar.reviews") },
  ];

  const programsDropdown = {
    icon: Edit3,
    label: t("admin.sidebar.programsGroup"),
    items: [
      { href: "/admin/creator-review", icon: Edit3, label: t("admin.sidebar.creator") },
      { href: "/admin/payouts", icon: DollarSign, label: t("admin.sidebar.payout") },
    ]
  };

  const settingsDropdown = {
    icon: Settings,
    label: t("admin.sidebar.settingsGroup"),
    items: [
      { href: "/admin/settings", icon: Settings, label: t("admin.sidebar.appSettings") },
      { href: "/admin/categories", icon: Tags, label: t("admin.sidebar.categories") },
      { href: "/admin/blog", icon: Rss, label: t("admin.sidebar.blog") },
      { href: "/admin/analytics", icon: BarChart3, label: t("admin.sidebar.analytics") },
    ]
  };
  
  // Determine which dropdowns should be open based on active route
  const isUsersActive = usersDropdown.items.some(item => location.pathname === item.href);
  const isProgramsActive = programsDropdown.items.some(item => location.pathname === item.href);
  const isSettingsActive = settingsDropdown.items.some(item => location.pathname === item.href);

  const [openDropdowns, setOpenDropdowns] = useState({
    users: isUsersActive,
    programs: isProgramsActive,
    settings: isSettingsActive,
  });

  // Update open state when route changes
  useEffect(() => {
    if (isUsersActive) setOpenDropdowns(prev => ({ ...prev, users: true }));
    if (isProgramsActive) setOpenDropdowns(prev => ({ ...prev, programs: true }));
    if (isSettingsActive) setOpenDropdowns(prev => ({ ...prev, settings: true }));
  }, [location.pathname, isUsersActive, isProgramsActive, isSettingsActive]);

  const toggleDropdown = (key: 'users' | 'programs' | 'settings') => {
    setOpenDropdowns(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  return (
    <>
      <SidebarHeader className="border-b-[3px] border-dark bg-white shadow-[0px_2px_0px_0px] shadow-dark p-4">
        <div className="flex items-center">
          <div className="flex-1">
            <h1 className="font-heading text-xl font-bold uppercase tracking-wide">Inclusia Admin</h1>
            <div className="text-xs text-muted-foreground mt-1 font-mono">
              {user?.email}
            </div>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="p-3">
        <SidebarMenu>
          {/* Top single items */}
          {topItems.map((item) => (
            <AdminMenuItem key={item.href} href={item.href} icon={item.icon}>
              {item.label}
            </AdminMenuItem>
          ))}

          {/* Users dropdown */}
          <AdminMenuDropdown
            icon={usersDropdown.icon}
            label={usersDropdown.label}
            items={usersDropdown.items}
            isOpen={openDropdowns.users}
            onToggle={() => toggleDropdown('users')}
          />

          {/* Middle single items */}
          {middleItems.map((item) => (
            <AdminMenuItem key={item.href} href={item.href} icon={item.icon}>
              {item.label}
            </AdminMenuItem>
          ))}

          {/* Programs dropdown */}
          <AdminMenuDropdown
            icon={programsDropdown.icon}
            label={programsDropdown.label}
            items={programsDropdown.items}
            isOpen={openDropdowns.programs}
            onToggle={() => toggleDropdown('programs')}
          />

          {/* Settings dropdown */}
          <AdminMenuDropdown
            icon={settingsDropdown.icon}
            label={settingsDropdown.label}
            items={settingsDropdown.items}
            isOpen={openDropdowns.settings}
            onToggle={() => toggleDropdown('settings')}
          />
        </SidebarMenu>
      </SidebarContent>
    </>
  );
};

export default function AdminLayout({ children }: AdminLayoutProps) {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      
      <SidebarProvider defaultOpen={false}>
        <div className="flex w-full bg-surface">
          {/* Desktop sidebar - sempre visibile */}
          <aside className="hidden lg:flex flex-col sticky top-0 h-screen w-64 border-r-[4px] border-dark shadow-[4px_0px_0px_0px] shadow-dark bg-sidebar">
            <AdminSidebarContent />
          </aside>

          {/* Mobile sidebar - Sheet overlay */}
          <Sidebar 
            className="lg:hidden border-r-[4px] border-dark shadow-[4px_0px_0px_0px] shadow-dark" 
            collapsible="offcanvas"
          >
            <AdminSidebarContent />
          </Sidebar>

          {/* Main content */}
          <main className="flex-1 overflow-auto">
            <header className="lg:hidden sticky top-0 z-10 flex h-14 items-center gap-4 border-b-[3px] border-dark bg-white px-4 shadow-[0px_2px_0px_0px] shadow-dark">
              <SidebarTrigger>
                <Menu className="h-5 w-5" />
              </SidebarTrigger>
              <h1 className="font-heading text-lg font-bold uppercase">Admin</h1>
            </header>
            {children}
          </main>
        </div>
      </SidebarProvider>
      
      <Footer />
    </div>
  );
}
